import React, { FC, useState } from 'react';
import { Headline4, Headline6, TextDefault } from '../general/Text';
import { AccordionBodyWrapper, SimpleAccordion } from '../general/SimpleAccordion';
import { Operation, OperationHistoryAddLead } from '../../interfaces/Operation';
import { FlexboxVertical } from '../general/Layout';
import styled from 'styled-components';
import { QuestionnaireContent } from '../lists/QuestionnaireContent';
import { LeadContent } from '../lists/LeadContent';
import { Customer, getAddressDisplayName } from '../../interfaces/Customer';
import { Product } from '../../interfaces/Product';
import { MinimalUser } from '../../interfaces/User';
import { Attachment } from '../../interfaces/Attachment';
import { DownloadButton } from '../general/DownloadButton';
import { useAttachment } from '../../utils/Attachments';

const FormWrapper = styled(FlexboxVertical)`
  ${Headline6} {
    padding: 1rem 2rem;
    
    div + & {
      margin-top: .5rem;
    }
  }
  
  ${Headline4} {
    font-size: 1rem;
  }
  
  ${AccordionBodyWrapper} {
    padding: 1rem 2rem 2rem;
  }
`;

const LeadFormWrapper = styled(FlexboxVertical)`
  margin-top: -2rem;

  & > ${Headline6} {
    padding: 1rem 0;
  }

  ${Headline6} {
    padding: 0;
  }
`;

interface Props {
  operation: Operation | null;
  summaryDoc?: Attachment | null;
  users: MinimalUser[];
  customers: Customer[];
  products: Product[];
}
export const OperationDetailsForm: FC<Props> = ({ operation, users, customers, products, ...props }) => {
  const [openAccordionsState, setOpenAccordionsState] = useState<string[]>([]);
  const isAccordionOpen = (id: string) => openAccordionsState.includes(id);
  const setAccordionOpen = (id: string, open: boolean) => {
    const nextState = openAccordionsState.filter((id) => id !== id);
    if (open) {
      nextState.push(id);
    }
    setOpenAccordionsState(nextState);
  }
  const toggleAccordionState = (id: string) => setAccordionOpen(id, !isAccordionOpen(id));

  const leads = (operation?.history || []).map((historyItem) => {
    if (historyItem.type === 'ADD_LEAD') {
      return (historyItem as OperationHistoryAddLead).lead;
    }
    return null;
  }).filter(Boolean);

  const customer = operation && customers.find(({ id }) => id === operation.customerId) || null;

  // const summaryDocument = useAttachment(operation?.summaryDocumentId);
  const summaryDocument = props.summaryDoc || null;

  return (
    <FormWrapper>
      <Headline6>Bedarfsanalyse</Headline6>
      <SimpleAccordion title={'Basisfragen'} collapsed={!isAccordionOpen('Q_1')} onClick={() => toggleAccordionState('Q_1')}>
        <QuestionnaireContent questions={operation?.questions || []} />
      </SimpleAccordion>
      {   // questions for additional addresses
        Object.keys(operation?.additionalQuestions || {}).map((key) => {
          const item = (operation?.additionalQuestions || {})[key];
          const address = (customer?.additionalAddresses || []).find((address, index) => `${index}` === key);
          return (
            <SimpleAccordion title={`Liegenschaftsbezogene Fragen: ${getAddressDisplayName(address)}`} collapsed={!isAccordionOpen(`QA_${key}`)} onClick={() => toggleAccordionState(`QA_${key}`)}>
              <QuestionnaireContent questions={item || []} />
            </SimpleAccordion>
          );
        })
      }
      <SimpleAccordion title={'Beratungsabschluss'} collapsed={!isAccordionOpen('Q_2')} onClick={() => toggleAccordionState('Q_2')}>
        <QuestionnaireContent questions={operation?.concludingQuestions || []} />
      </SimpleAccordion>

      {
        summaryDocument &&
        <>
        <Headline6>Dokumente</Headline6>
        <SimpleAccordion title={'Gesprächsprotokoll als Word-Datei (.docx)'} collapsed={!isAccordionOpen(`DOC_protocol_word`)} onClick={() => toggleAccordionState(`DOC_protocol_word`)}>
          <DownloadButton label="Download" file={summaryDocument.dataUrl} filename={summaryDocument.fileName} />
        </SimpleAccordion>
        </>
      }

      {     // notes
        operation?.notes?.length > 0 &&
        <>
        <Headline6>Notizen</Headline6>
        {
          operation?.notes?.map((note) => (
            <SimpleAccordion title={note.subject} collapsed={!isAccordionOpen(`NOTE_${note.subject}_${note.id}`)} onClick={() => toggleAccordionState(`NOTE_${note.subject}_${note.id}`)}>
              { note.body }
            </SimpleAccordion>
          ))
        }
        </>
      }

      {     // leads
        leads.length > 0 &&
        <>
        <Headline6>Leads</Headline6>
        {
          leads.map((lead, index) => (
            <SimpleAccordion title={`Lead ${index+1}`} collapsed={!isAccordionOpen(`LEAD_${lead.id}`)} onClick={() => toggleAccordionState(`LEAD_${lead.id}`)}>
              <LeadFormWrapper>
                <LeadContent
                  lead={lead}
                  users={users}
                  customers={customers}
                  products={products}
                />

                <Headline6>Lead-Fragen</Headline6>
                <QuestionnaireContent questions={lead?.questions || []} />
              </LeadFormWrapper>
            </SimpleAccordion>
          ))
        }
        </>
      }
    </FormWrapper>
  )
}
